/**
 * iter 73.14 Phase C — Route Builder.
 *
 * Bottom half of the Command Center's right panel. Users can:
 *   • Pick an existing route or create a new one
 *   • Add jobs to the current route (via the "add-to-route" button in
 *     the Job Queue → forwarded into this component's `onAddJob` API)
 *   • Drag-and-drop reorder stops (HTML5 native DnD)
 *   • See per-leg + total distance/time via `google.maps.DistanceMatrixService`
 *     (browser SDK, no CORS issues, no server-side proxy needed)
 *   • Assign a driver + save → driver gets an in-app push (activity_events row)
 *
 * Deletions cascade because the backend PATCH replaces the whole stops
 * list atomically.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Path as PathIcon, Plus, Trash, DotsSixVertical, X as XIcon,
  Ruler, Clock as ClockIcon, PaperPlaneTilt, ArrowsClockwise,
  MagicWand, Warning,
} from "@phosphor-icons/react";
import { api, formatErr } from "../lib/api";

function useDistanceLegs(stopsWithCoords) {
  // Computes leg distance/duration between consecutive stops using
  // google.maps.DistanceMatrixService (loaded by the map). Memoised by
  // the sequence of "lat,lng" strings so re-orders trigger recompute.
  const [legs, setLegs] = useState([]);
  const [computing, setComputing] = useState(false);
  const key = stopsWithCoords
    .map((s) => `${s.latitude},${s.longitude}`)
    .join("|");

  useEffect(() => {
    if (!window.google?.maps?.DistanceMatrixService) {
      setLegs([]);
      return;
    }
    const valid = stopsWithCoords.filter(
      (s) => Number.isFinite(s.latitude) && Number.isFinite(s.longitude)
    );
    if (valid.length < 2) {
      setLegs([]);
      return;
    }
    let cancelled = false;
    setComputing(true);
    const svc = new window.google.maps.DistanceMatrixService();
    const origins = valid.slice(0, -1).map((s) => ({ lat: s.latitude, lng: s.longitude }));
    const destinations = valid.slice(1).map((s) => ({ lat: s.latitude, lng: s.longitude }));
    svc.getDistanceMatrix(
      {
        origins, destinations,
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.IMPERIAL,
      },
      (res, status) => {
        if (cancelled) return;
        setComputing(false);
        if (status !== "OK" || !res?.rows) {
          setLegs([]);
          return;
        }
        // Diagonal picks — origin[i] → destination[i].
        const out = res.rows.map((row, i) => {
          const el = row.elements[i];
          if (!el || el.status !== "OK") return null;
          return {
            miles: el.distance.value / 1609.344,
            minutes: el.duration.value / 60,
            text_distance: el.distance.text,
            text_duration: el.duration.text,
          };
        });
        setLegs(out);
      }
    );
    return () => { cancelled = true; };
  }, [key]);

  const total = useMemo(() => {
    const valid = legs.filter(Boolean);
    return {
      miles:   valid.reduce((a, b) => a + b.miles, 0),
      minutes: valid.reduce((a, b) => a + b.minutes, 0),
    };
  }, [legs]);

  return { legs, total, computing };
}

export default function RouteBuilder({ drivers, primary, addedJobs, onConsumed }) {
  const [routes, setRoutes] = useState([]);
  const [activeId, setActiveId] = useState(null);   // route being edited
  const [route, setRoute] = useState(null);          // full route object with stops
  const [saving, setSaving] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  // iter 73.16 — Smart Route Optimization.
  //
  // Two-pass approach per spec:
  //   Pass 1: Sort stops by `lar_due_at` ascending — earliest deadline
  //           first (stops with no due_at fall to the end).
  //   Pass 2: Group into deadline buckets (per calendar day) and, within
  //           each bucket, call google.maps.DirectionsService with
  //           `optimizeWaypoints:true` to minimise mileage. Concatenate.
  //
  // Result is written into local `route.stops` (not persisted until user
  // hits Save). Warning badges on the individual stop rows show which
  // ones the ETA math still predicts will breach their lar_due_at.
  const optimizeRoute = useCallback(async () => {
    if (!route?.stops?.length) return;
    if (!window.google?.maps?.DirectionsService) {
      toast.error("Google Maps is still loading — try again in a moment.");
      return;
    }
    const valid = route.stops.filter(
      (s) => Number.isFinite(s.latitude) && Number.isFinite(s.longitude)
    );
    if (valid.length < 3) {
      toast("Need at least 3 stops with coordinates to optimize.", { icon: "ℹ️" });
      return;
    }
    setOptimizing(true);
    try {
      // Pass 1: sort by deadline (nulls last).
      const withKey = valid.map((s) => ({
        ...s,
        _bucket: s.lar_due_at ? new Date(s.lar_due_at).toISOString().slice(0, 10) : "zz-none",
      }));
      withKey.sort((a, b) => {
        if (a._bucket === b._bucket) return 0;
        return a._bucket < b._bucket ? -1 : 1;
      });
      // Bucket rows by day.
      const bucketMap = new Map();
      for (const s of withKey) {
        const arr = bucketMap.get(s._bucket) || [];
        arr.push(s);
        bucketMap.set(s._bucket, arr);
      }
      // Pass 2: per-bucket, run optimizeWaypoints:true.
      const svc = new window.google.maps.DirectionsService();
      const optimizedBuckets = [];
      for (const [, bucket] of bucketMap) {
        if (bucket.length < 3) {
          // 1-2 stops → nothing to reorder; keep as-is.
          optimizedBuckets.push(bucket);
          continue;
        }
        const origin = { lat: bucket[0].latitude, lng: bucket[0].longitude };
        const destination = { lat: bucket[bucket.length - 1].latitude, lng: bucket[bucket.length - 1].longitude };
        const waypoints = bucket.slice(1, -1).map((s) => ({
          location: { lat: s.latitude, lng: s.longitude },
          stopover: true,
        }));
        // eslint-disable-next-line no-await-in-loop
        const result = await new Promise((resolve) => {
          svc.route(
            {
              origin, destination, waypoints,
              optimizeWaypoints: true,
              travelMode: window.google.maps.TravelMode.DRIVING,
            },
            (res, status) => resolve(status === "OK" ? res : null)
          );
        });
        if (!result || !result.routes?.[0]) {
          // Fallback: keep original order.
          optimizedBuckets.push(bucket);
          continue;
        }
        const order = result.routes[0].waypoint_order || [];
        // Reconstitute: [origin, …middle-in-optimized-order…, destination]
        const middle = order.map((idx) => bucket[idx + 1]);
        optimizedBuckets.push([bucket[0], ...middle, bucket[bucket.length - 1]]);
      }
      // Include any stops missing coords (couldn't optimize) at the end.
      const noCoords = route.stops.filter(
        (s) => !(Number.isFinite(s.latitude) && Number.isFinite(s.longitude))
      );
      const flat = [...optimizedBuckets.flat(), ...noCoords].map((s) => {
        const { _bucket, ...rest } = s; // strip helper
        return rest;
      });
      setRoute({ ...route, stops: flat });
      toast.success("Route optimized. Review + hit Save to notify the driver.");
    } catch (e) {
      toast.error("Optimization failed — try again.");
    } finally {
      setOptimizing(false);
    }
  }, [route]);
  const dragIndexRef = useRef(null);

  const loadRoutes = useCallback(async () => {
    try {
      const r = await api.get("/command-center/routes");
      setRoutes(Array.isArray(r.data) ? r.data : []);
    } catch { setRoutes([]); }
  }, []);

  useEffect(() => { loadRoutes(); }, [loadRoutes]);

  const loadRoute = useCallback(async (id) => {
    if (!id) { setRoute(null); return; }
    try {
      // No single-GET endpoint — reuse list then filter; small n so OK.
      const r = await api.get("/command-center/routes");
      const match = (r.data || []).find((x) => x.id === id);
      if (!match) { setRoute(null); return; }
      // Fetch stops via a follow-up PATCH-noop (returns the full route
      // including stops). Cheaper: expose GET /{id} — TODO.
      // For now, do a trivial PATCH that changes nothing but returns
      // the serialised route with stops.
      const upd = await api.patch(`/command-center/routes/${id}`, {});
      setRoute(upd.data);
    } catch (e) {
      toast.error(formatErr(e));
    }
  }, []);

  useEffect(() => { loadRoute(activeId); }, [activeId, loadRoute]);

  // Jobs pushed in from the parent Job Queue → append as stops.
  useEffect(() => {
    if (!addedJobs || addedJobs.length === 0 || !route) return;
    setRoute((cur) => {
      if (!cur) return cur;
      const existing = new Set(cur.stops.map((s) => s.job_id).filter(Boolean));
      const next = [...cur.stops];
      for (const j of addedJobs) {
        if (j.id && !existing.has(j.id) && j.latitude != null && j.longitude != null) {
          next.push({
            id: `tmp-${j.id}`,
            job_id: j.id,
            location_id: j.location_id,
            location_name: j.location_name,
            latitude: j.latitude,
            longitude: j.longitude,
            job_number: j.job_number,
          });
        }
      }
      return { ...cur, stops: next };
    });
    onConsumed?.();
  }, [addedJobs, route, onConsumed]);

  const createRoute = async () => {
    const name = `Route ${new Date().toLocaleDateString()} — ${new Date().toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}`;
    try {
      const r = await api.post("/command-center/routes", { name, stops: [] });
      await loadRoutes();
      setActiveId(r.data.id);
      toast.success("Route created.");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const removeStop = (idx) => {
    setRoute((cur) => ({ ...cur, stops: cur.stops.filter((_, i) => i !== idx) }));
  };

  const onDragStart = (idx) => { dragIndexRef.current = idx; };
  const onDragOver = (e) => { e.preventDefault(); };
  const onDrop = (idx) => {
    const from = dragIndexRef.current;
    dragIndexRef.current = null;
    if (from == null || from === idx) return;
    setRoute((cur) => {
      const next = [...cur.stops];
      const [moved] = next.splice(from, 1);
      next.splice(idx, 0, moved);
      return { ...cur, stops: next };
    });
  };

  const { legs, total, computing } = useDistanceLegs(route?.stops || []);

  // iter 73.15 Phase G — Departure time + per-stop ETA. User picks a
  // departure clock time (defaults to now, rounded up to the next 5min);
  // we propagate through leg durations to compute an ETA at each stop.
  const [departureAt, setDepartureAt] = useState(() => {
    const d = new Date();
    d.setMinutes(Math.ceil(d.getMinutes() / 5) * 5, 0, 0);
    return d.toTimeString().slice(0, 5); // HH:MM
  });
  const stopEtas = useMemo(() => {
    if (!route?.stops?.length || !departureAt) return [];
    const [hh, mm] = departureAt.split(":").map(Number);
    let cur = new Date();
    cur.setHours(hh, mm, 0, 0);
    const etas = [new Date(cur)];
    for (let i = 0; i < route.stops.length - 1; i++) {
      const leg = legs[i];
      const mins = leg?.minutes ?? 0;
      cur = new Date(cur.getTime() + mins * 60000);
      etas.push(new Date(cur));
    }
    return etas.map((d) => d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
  }, [route?.stops?.length, legs, departureAt]);

  // iter 73.16 — Predicted-breach flag per stop. Compares the ETA
  // (departure + cumulative travel time + expected_duration_minutes at
  // each prior stop) against the stop's `lar_due_at`. When the ETA is
  // *after* the deadline, we mark the stop as breaching.
  const stopEtaMs = useMemo(() => {
    if (!route?.stops?.length || !departureAt) return [];
    const [hh, mm] = departureAt.split(":").map(Number);
    const cur = new Date();
    cur.setHours(hh, mm, 0, 0);
    const out = [cur.getTime()];
    let acc = cur.getTime();
    for (let i = 0; i < route.stops.length - 1; i++) {
      const leg = legs[i];
      const travel = leg?.minutes ?? 0;
      const dwell = route.stops[i]?.expected_duration_minutes ?? 15;
      acc = acc + (travel + dwell) * 60000;
      out.push(acc);
    }
    return out;
  }, [route?.stops, legs, departureAt]);
  const breachedIds = useMemo(() => {
    const s = new Set();
    (route?.stops || []).forEach((stop, i) => {
      if (!stop.lar_due_at) return;
      const dueMs = new Date(stop.lar_due_at).getTime();
      if (Number.isFinite(dueMs) && stopEtaMs[i] > dueMs) s.add(stop.id || i);
    });
    return s;
  }, [route?.stops, stopEtaMs]);

  const save = async () => {
    if (!route) return;
    setSaving(true);
    try {
      const upd = await api.patch(`/command-center/routes/${route.id}`, {
        name: route.name,
        assigned_user_id: route.assigned_user_id || null,
        stops: route.stops.map((s) => ({
          location_id: s.location_id, job_id: s.job_id, notes: s.notes,
        })),
        total_distance_miles: Number(total.miles.toFixed(2)) || null,
      });
      setRoute(upd.data);
      await loadRoutes();
      toast.success(route.assigned_user_id
        ? "Route saved. Driver notified."
        : "Route saved.");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const deleteRoute = async () => {
    if (!route) return;
    if (!window.confirm(`Delete route "${route.name}"?`)) return;
    try {
      await api.delete(`/command-center/routes/${route.id}`);
      setActiveId(null);
      setRoute(null);
      await loadRoutes();
      toast.success("Route deleted.");
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="h-full flex flex-col bg-card" data-testid="cc-route-builder">
      <div
        className="px-3 py-2 border-b border-border flex items-center gap-2 text-[11px] mono uppercase text-muted-foreground tracking-wide"
        style={{ borderTopColor: primary, borderTopWidth: 2 }}
      >
        <PathIcon size={12} /> ROUTE BUILDER
        {/* iter 73.15 Phase G — live totals in the header. Shows the
            current route's total distance + time, updated in real time
            as the user drags stops. */}
        {route && route.stops.length >= 2 && (
          <span
            className="ml-2 text-[10px] font-normal normal-case text-foreground/80 flex items-center gap-1.5"
            data-testid="cc-route-header-totals"
            title={`${total.miles.toFixed(1)} mi · ${Math.round(total.minutes)} min`}
          >
            <Ruler size={9} /> {total.miles.toFixed(1)}mi
            <ClockIcon size={9} /> {Math.round(total.minutes)}min
            {computing && <ArrowsClockwise size={9} className="animate-spin" />}
          </span>
        )}
        <button
          type="button"
          onClick={createRoute}
          className="ml-auto btn btn-ghost btn-xs"
          data-testid="cc-route-new"
          title="New route"
        ><Plus size={11} /></button>
        {/* iter 73.16 — Optimize Route. Two-pass: deadline-buckets +
            google.maps optimizeWaypoints for min mileage per bucket. */}
        {route && route.stops.length >= 3 && (
          <button
            type="button"
            onClick={optimizeRoute}
            disabled={optimizing}
            className="btn btn-ghost btn-xs disabled:opacity-50"
            data-testid="cc-route-optimize"
            title="Optimize route order — sort by deadline, minimise mileage within each day"
          >
            <MagicWand size={11} className={optimizing ? "animate-pulse" : ""} />
          </button>
        )}
      </div>

      {/* Route picker */}
      <div className="px-3 py-2 border-b border-border">
        <select
          className="input text-xs"
          value={activeId || ""}
          onChange={(e) => setActiveId(e.target.value || null)}
          data-testid="cc-route-picker"
        >
          <option value="">— pick a route —</option>
          {routes.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name} ({r.stop_count} stops)
            </option>
          ))}
        </select>
      </div>

      {!route && (
        <div className="text-xs text-muted-foreground italic p-3 text-center">
          {routes.length === 0 ? "No routes yet. Click + to create one." : "Pick a route above to edit."}
        </div>
      )}

      {route && (
        <>
          <div className="px-3 py-2 border-b border-border space-y-2">
            <input
              type="text"
              className="input text-xs"
              value={route.name || ""}
              onChange={(e) => setRoute({ ...route, name: e.target.value })}
              placeholder="Route name"
              data-testid="cc-route-name"
            />
            <select
              className="input text-xs"
              value={route.assigned_user_id || ""}
              onChange={(e) => setRoute({ ...route, assigned_user_id: e.target.value || null })}
              data-testid="cc-route-driver"
            >
              <option value="">— assign driver —</option>
              {drivers.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            {/* iter 73.15 Phase G — Departure time picker → propagates
                ETAs to each stop row below. */}
            <div className="flex items-center gap-2 text-[10px] mono uppercase text-muted-foreground">
              <ClockIcon size={10} />
              <span>Depart</span>
              <input
                type="time"
                value={departureAt}
                onChange={(e) => setDepartureAt(e.target.value)}
                className="bg-transparent border-b border-border outline-none text-xs mono normal-case"
                data-testid="cc-route-departure"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto" data-testid="cc-route-stops">
            {route.stops.length === 0 && (
              <div className="text-xs text-muted-foreground italic p-3 text-center">
                No stops yet. Click a job pin then + to add.
              </div>
            )}
            {route.stops.map((s, i) => (
              <div key={s.id || i}>
                <div
                  className="flex items-center gap-1.5 px-3 py-1.5 border-b border-border hover:bg-muted/40 group"
                  draggable
                  onDragStart={() => onDragStart(i)}
                  onDragOver={onDragOver}
                  onDrop={() => onDrop(i)}
                  data-testid={`cc-route-stop-${i}`}
                >
                  <DotsSixVertical size={12} className="text-muted-foreground cursor-move flex-shrink-0" />
                  <span className="text-[10px] mono text-muted-foreground w-4 flex-shrink-0">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs truncate leading-tight">
                      {s.job_number ? `Job ${s.job_number}` : (s.location_name || "Location")}
                    </div>
                    {s.location_name && s.job_number && (
                      <div className="text-[10px] text-muted-foreground truncate">{s.location_name}</div>
                    )}
                  </div>
                  {breachedIds.has(s.id || i) && (
                    <span
                      className="flex items-center gap-0.5 text-[10px] mono uppercase text-red-600 dark:text-red-400 bg-red-500/15 rounded-full px-1.5 py-0.5 flex-shrink-0"
                      title={`Predicted arrival (${stopEtas[i] || "?"}) is after the attempt-window deadline (${new Date(s.lar_due_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}).`}
                      data-testid={`cc-route-stop-breach-${i}`}
                    >
                      <Warning size={10} weight="fill" />
                      BREACH
                    </span>
                  )}
                  {stopEtas[i] && (
                    <span
                      className={`text-[10px] mono flex-shrink-0 ${breachedIds.has(s.id || i) ? "text-red-600 dark:text-red-400" : "text-muted-foreground"}`}
                      title="Estimated arrival time"
                      data-testid={`cc-route-stop-eta-${i}`}
                    >{stopEtas[i]}</span>
                  )}
                  <button
                    onClick={() => removeStop(i)}
                    className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
                    title="Remove"
                    data-testid={`cc-route-stop-remove-${i}`}
                  ><Trash size={11} /></button>
                </div>
                {i < route.stops.length - 1 && legs[i] && (
                  <div
                    className="flex items-center gap-2 px-3 py-1 border-b border-border bg-muted/10 text-[10px] mono text-muted-foreground"
                    data-testid={`cc-route-leg-${i}`}
                  >
                    <span className="w-4 flex-shrink-0" />
                    <Ruler size={9} /> {legs[i].text_distance}
                    <ClockIcon size={9} /> {legs[i].text_duration}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Totals + save */}
          <div className="px-3 py-2 border-t border-border space-y-2 bg-muted/10">
            <div className="flex items-center justify-between text-[11px] mono" data-testid="cc-route-totals">
              <span className="text-muted-foreground">TOTAL</span>
              <span>
                {total.miles.toFixed(1)} mi · {Math.round(total.minutes)} min
                {computing && <ArrowsClockwise size={9} className="animate-spin inline ml-1" />}
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={deleteRoute}
                className="btn btn-ghost btn-xs text-destructive"
                data-testid="cc-route-delete"
              ><XIcon size={11} /></button>
              <button
                onClick={save}
                disabled={saving}
                className="btn btn-primary btn-xs flex-1 disabled:opacity-50"
                style={{ background: primary }}
                data-testid="cc-route-save"
              >
                <PaperPlaneTilt size={11} className="mr-1" />
                {saving ? "Saving…" : (route.assigned_user_id ? "Save & Notify" : "Save")}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
